import React, { useEffect, useState } from "react"
import { Button, Card, Form, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../../lib/GlobalMethods"
import { modulePermission } from "../../../lib/GlobalVariables"
import useDebounce from "../../../lib/hooks/useDebounce"
import ConnectorsSkeleton from "../../../skeleton/settings/ConnectorsSkeleton"
import AddEditConnector from "./AddEditConnector"
import InfiniteScroll from "react-infinite-scroll-component"

const ConnectorsManagement = () => {
  const canEdit = HasModulePermission(modulePermission.setting, "edit")
  const canDelete = HasModulePermission(modulePermission.setting, "delete")
  const canAdd = HasModulePermission(modulePermission.setting, "add")

  const [show, setShow] = useState(false)
  const [data, setData] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    inactive_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [filterObj, setFilterObj] = useState({
    status: false,
  })

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const handleShow = () => setShow(true)

  const handleShowAlert = () => setShowAlert(true)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)

    setShowAlert(false)
    setIsDelete(false)
  }

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleStatusChange = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active"
      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateConnector + id,
        data: { status: newStatus },
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        setData((data) =>
          data.map((user) =>
            user.id === id ? { ...user, status: newStatus } : user
          )
        )
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.connectorList}?page=${pagination.current_page}`,
      })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      handleAccess({ code: response?.data?.code })

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData]
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error)
    }
  }
  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: admin.deleteConnector + id,
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((data) => data.filter((user) => user.id !== id))
        setData((data) =>
          data.sort((value, nextValue) => nextValue.id - value.id)
        )
        setLoading(false)
      } else {
        setLoading(false)
        throw { message: response.data.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const selectBrandStatus = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ]

  const [open, setOpen] = useState(false)
  const filterOnClick = () => {
    setOpen(!open)
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText={"Connectors Management"}
          HeaderButtonText={"Add Connectors"}
          IconclassName={"plus_black"}
          onClick={handleShow}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Connectors Management"}
          onClick={handleShow}
        />
      )}
      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<ConnectorsSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th className="text-center">Image</th>
                    <th>Connector Name</th>
                    <TooltipComponent
                      toolplace="top"
                      tooltipText="Max Output Power (kW)"
                    >
                      <th className="text-center">MOP (kW)</th>
                    </TooltipComponent>
                    <TooltipComponent
                      toolplace="top"
                      tooltipText="Max Current Output (Amps)"
                    >
                      <th className="text-center">MCO (Amps)</th>
                    </TooltipComponent>
                    <th className="text-center">Charging level</th>
                    <th className="text-center">Current Type</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 &&
                    data?.map((data, index) => (
                      <tr key={index + 1}>
                        <td className="text-center">
                          <img
                            src={
                              data.connector_img
                                ? process.env.REACT_APP_RESOURCE_URL +
                                  data.connector_img
                                : "https://placehold.co/50x50.png"
                            }
                            alt={data.connector_type}
                            title={data.connector_type}
                            className="connector_img"
                          />
                        </td>
                        <td>{data.connector_type}</td>
                        <td className="text-center">{data.max_power_output}</td>
                        <td className="text-center">
                          {data.max_current_output}
                        </td>
                        <td className="text-center">Level {data.level}</td>
                        <td className="text-center">{data.current_type}</td>
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Edit"
                                >
                                  <Button
                                    variant="btnedit_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      handleShow()
                                      setIsUpdate(data)
                                    }}
                                  >
                                    <i className="icon_main edit_pencil"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                              {canDelete && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Delete"
                                >
                                  <Button
                                    variant="btndelete_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setShowAlert(true)
                                      setIsUpdate(data)
                                      setIsDelete(true)
                                    }}
                                  >
                                    <i className="icon_main delete_trash"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {(data?.length === 0 || data?.length === undefined) && (
                    <tr>
                      <td className="text-center py-4" colSpan={8}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <ConnectorsSkeleton />
      )}

      <AddEditConnector
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
        setData={setData}
        setIsDelete={setIsDelete}
        setShowAlert={setShowAlert}
      />

      <SweetAlerts
        showAlert={showAlert}
        alertMessage={
          isDelete
            ? "Are you sure you want to Delete this Connector ?"
            : `Are you sure you want to ${
                isUpdate.status === "Active" ? "Inactive" : "Active"
              } this Connector ?`
        }
        handleClose={handleClose}
        iconClassName={isDelete ? "delete_trash" : "exclamation_circle"}
        handleAlert={() => {
          isDelete
            ? handleDelete(isUpdate.id)
            : handleStatusChange(isUpdate.id, isUpdate.status)
        }}
      />
    </>
  )
}

export default ConnectorsManagement
